import React from "react";
import { Link } from "react-router-dom";
import SupabaseTest from "../components/SupabaseTest";
import { SHORTCUTS, formatShortcut } from "../utils/keyboardShortcuts";

const Help = () => {
  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="bg-[#1a1e21] rounded-lg shadow-lg p-6 mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-[#4db1e0] mb-6">
          ELITA Kanban Help
        </h1>

        <div className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-4 border-b border-gray-700 pb-2">
            Database Connection Status
          </h2>
          <SupabaseTest />
        </div>

        <div className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-4 border-b border-gray-700 pb-2">
            Keyboard Shortcuts
          </h2>
          <p className="text-gray-300 mb-4">
            Press{" "}
            <kbd className="px-2 py-1 bg-[#262b30] rounded text-sm">?</kbd>{" "}
            anywhere in the application to view these shortcuts.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Object.entries(SHORTCUTS).map(([category, shortcuts]) => (
              <div key={category} className="bg-[#262b30] rounded-lg p-4">
                <h3 className="font-medium text-[#4db1e0] mb-3">{category}</h3>
                <div className="space-y-2">
                  {Object.entries(shortcuts).map(
                    ([name, { key, description }]) => (
                      <div key={name} className="flex justify-between text-sm">
                        <span className="text-gray-300">{description}</span>
                        <kbd className="px-2 py-1 ml-3 bg-[#1a1e21] text-gray-200 rounded text-xs font-mono">
                          {formatShortcut(key)}
                        </kbd>
                      </div>
                    )
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-4 border-b border-gray-700 pb-2">
            Mobile Features
          </h2>

          <div className="bg-[#262b30] rounded-lg p-4 mb-4">
            <h3 className="font-medium text-[#4db1e0] mb-3">Touch Controls</h3>
            <ul className="list-disc pl-5 text-gray-300 space-y-2">
              <li>
                <span className="text-white">Tap a task</span> to edit it
              </li>
              <li>
                <span className="text-white">Long-press and drag</span> to move
                a task between columns
              </li>
              <li>
                <span className="text-white">Swipe horizontally</span> to
                navigate between columns
              </li>
              <li>
                <span className="text-white">Tap the hamburger icon</span> in
                the top-right to access the navigation menu
              </li>
            </ul>
          </div>

          <div className="bg-[#262b30] rounded-lg p-4">
            <h3 className="font-medium text-[#4db1e0] mb-3">
              Responsive Design
            </h3>
            <ul className="list-disc pl-5 text-gray-300 space-y-2">
              <li>The interface automatically adapts to your screen size</li>
              <li>
                On small screens, columns stack vertically for easier navigation
              </li>
              <li>
                The mobile helper tooltip provides context-sensitive guidance
              </li>
              <li>All buttons are sized for comfortable touch targets</li>
            </ul>
          </div>
        </div>

        <div className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-4 border-b border-gray-700 pb-2">
            General Usage
          </h2>

          <div className="bg-[#262b30] rounded-lg p-4">
            <h3 className="font-medium text-[#4db1e0] mb-3">Task Management</h3>
            <ul className="list-disc pl-5 text-gray-300 space-y-2">
              <li>
                Create new tasks in the{" "}
                <Link to="/intake" className="text-[#4db1e0] hover:underline">
                  Intake
                </Link>{" "}
                section
              </li>
              <li>
                Brainstorm ideas in the{" "}
                <Link to="/workshop" className="text-[#4db1e0] hover:underline">
                  Workshop
                </Link>
              </li>
              <li>Move tasks between columns to update their status</li>
              <li>Click/tap a task to edit its details</li>
              <li>Tasks in the "Done" column can be deleted or archived</li>
              <li>
                Use the "Archive All Done" button to move completed tasks to the
                Archive
              </li>
              <li>
                Toggle the "Show Archived" button to view or hide archived tasks
              </li>
            </ul>
          </div>
        </div>

        <div className="flex justify-center mt-8">
          <Link
            to="/"
            className="px-4 py-2 bg-[#4db1e0] text-white rounded-md hover:bg-[#3da0cf] transition-colors duration-200"
          >
            Return to Dashboard
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Help;
